import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import groupBy from 'lodash.groupby';
import { Response, Currency } from '@ui/src';
import {
  RiskScoreCalculationType,
  TransactionVolume,
  ClientType,
  TransactionVolumeTypeEnum,
  TransactionVolumeRiskListItem,
} from '../../../types';
import { transactionsRiskApi } from '../services';

export interface FormValues {
  factorsForIndividualClients: {
    calculate: RiskScoreCalculationType;
    singleTransaction?: string;
    multipleTransaction?: string;
  };
  factorsForCorporateClients: {
    calculate: RiskScoreCalculationType;
    singleTransaction?: string;
    multipleTransaction?: string;
  };
}

export interface TransactionsVolumesRiskListState {
  isLoading: boolean;
  requestInProgress?: boolean;
  formRequestInProgress: boolean;
  values?: TransactionVolumeRiskListItem[];
  transactionVolumes?: TransactionVolumeRiskListItem[];
  baseCurrency?: Currency;
}

const initialState: TransactionsVolumesRiskListState = {
  isLoading: true,
  requestInProgress: false,
  formRequestInProgress: false,
};

export const transactionsVolumesRiskListSlice = createSlice({
  name: 'transactionsVolumesRiskList',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addMatcher(
      transactionsRiskApi.endpoints.getTransactionVolumes.matchFulfilled,
      (state, action: PayloadAction<Response<TransactionVolume>>) => {
        const formatted = action.payload.data.edges.map((item) => item.node);

        const grouped = Object.values(
          groupBy(formatted, (match) => match.riskScore),
        );

        const result: TransactionVolumeRiskListItem[] = [];

        grouped.forEach((item) => {
          const individualVolumes = item.filter(
            (volume) => volume.customer === ClientType.INDIVIDUAL,
          );
          const corporateVolumes = item.filter(
            (volume) => volume.customer === ClientType.CORPORATE,
          );

          const body: TransactionVolumeRiskListItem = {
            id: item[0].id,
            riskCategory: item[0].riskCategory,
            score: item[0].riskScore,
          };

          individualVolumes.forEach((volume) => {
            if (volume.type === TransactionVolumeTypeEnum.SINGLE) {
              body.individualSingleTransaction = {
                id: volume.id,
                from: volume.min,
                to: volume.max,
                currency: volume.currency || '',
              };
            }

            if (volume.type === TransactionVolumeTypeEnum.MULTIPLE) {
              body.individualMultiTransaction = {
                id: volume.id,
                from: volume.min,
                to: volume.max,
                currency: volume.currency || '',
              };
            }
          });

          corporateVolumes.forEach((volume) => {
            if (volume.type === TransactionVolumeTypeEnum.SINGLE) {
              body.corporateSingleTransacion = {
                id: volume.id,
                from: volume.min,
                to: volume.max,
                currency: volume.currency || '',
              };
            }

            if (volume.type === TransactionVolumeTypeEnum.MULTIPLE) {
              body.corporateMultiTransaction = {
                id: volume.id,
                from: volume.min,
                to: volume.max,
                currency: volume.currency || '',
              };
            }
          });

          result.push(body);
        });

        state.transactionVolumes = result;
      },
    );
    builder.addMatcher(
      transactionsRiskApi.endpoints.getAmlBaseCurrency.matchFulfilled,
      (state, action: PayloadAction<{ data: Currency }>) => {
        state.baseCurrency = action.payload.data;
      },
    );
  },
});

export default transactionsVolumesRiskListSlice.reducer;
